import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { PaletteMode, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeMode, createAppTheme } from '../theme';
import { useLocalStorage } from '../hooks/useLocalStorage';

interface ThemeContextType {
  mode: ThemeMode;
  actualMode: PaletteMode; // The actual mode being used (light/dark, resolved from system)
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const { value: storedMode, setValue: setStoredMode } = useLocalStorage<ThemeMode>('theme-mode', 'system');
  const [mode, setMode] = useState<ThemeMode>(storedMode);
  const [systemPreference, setSystemPreference] = useState<PaletteMode>(() =>
    window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  );

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      setSystemPreference(e.matches ? 'dark' : 'light');
    };

    // Set initial value
    setSystemPreference(mediaQuery.matches ? 'dark' : 'light');

    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Update stored mode when mode changes
  useEffect(() => {
    setStoredMode(mode);
  }, [mode, setStoredMode]);

  // Calculate the actual mode to use
  const actualMode: PaletteMode = mode === 'system' ? systemPreference : mode;

  const handleSetMode = (newMode: ThemeMode) => {
    setMode(newMode);
  };

  const toggleMode = () => {
    setMode(actualMode === 'dark' ? 'light' : 'dark');
  };

  const value: ThemeContextType = {
    mode,
    actualMode,
    setMode: handleSetMode,
    toggleMode,
  };

  const theme = React.useMemo(() => createAppTheme(actualMode), [actualMode]);

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}